import { useEffect, useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchArticles } from "@/lib/api";

export default function SignalLibrary() {
  const { ref, visible } = useReveal();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetchArticles()
      .then(setArticles)
      .catch(() => setArticles([]));
  }, []);

  return (
    <section
      id="signal-library"
      ref={ref}
      data-testid="signal-library-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="absolute inset-0 bg-grid-fine opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="num-label">SECTION 08 / SIGNAL LIBRARY</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <h2
              data-testid="signal-library-headline"
              className={`font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] reveal ${
                visible ? "in" : ""
              }`}
            >
              The Signal Library
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className={`text-axiom-muted text-base sm:text-lg leading-relaxed reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              Axiom-AI's thought leadership is written like field intelligence,
              not casual blog content. Each article helps leaders interpret
              the operational signals that reveal hidden cost, network
              friction, and decision failure.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {articles.map((a, i) => (
            <button
              key={a.slug}
              data-testid={`article-card-${a.slug}`}
              onClick={() => navigate(`/signal-library/${a.slug}`)}
              className={`bg-axiom-bg p-6 md:p-7 text-left hover:bg-axiom-surface transition-colors duration-300 group reveal ${
                visible ? "in" : ""
              } min-h-[260px] flex flex-col`}
              style={{ transitionDelay: `${100 + i * 70}ms` }}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="num-label text-axiom-accent">{a.eyebrow}</span>
                <ArrowUpRight
                  size={16}
                  className="text-axiom-muted group-hover:text-axiom-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                />
              </div>
              <h3 className="font-display text-lg md:text-xl text-axiom-text font-bold leading-snug">
                {a.title}
              </h3>
              <p className="mt-3 text-sm text-axiom-muted leading-relaxed line-clamp-3">
                {a.excerpt}
              </p>
              <div className="mt-auto pt-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/10" />
                <span className="num-label">{a.read_time}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <button
            data-testid="signal-library-view-all"
            onClick={() => navigate("/signal-library")}
            className="btn-axiom-outline text-sm"
          >
            View the full archive
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
